import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { AuthService } from '../../services/authService';

interface PermissionRouteProps {
  children: React.ReactNode;
  requiredModule: string;
  requiredPermission?: 'view' | 'edit' | 'delete' | 'all';
  fallbackPath?: string;
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({ 
  children, 
  requiredModule, 
  requiredPermission = 'view',
  fallbackPath = '/dashboard'
}) => {
  const location = useLocation();

  const permissionCheck = useMemo(() => {
    console.log('🔍 === PERMISSION ROUTE CHECK START ===');
    console.log('Required module:', requiredModule);
    console.log('Required permission:', requiredPermission);
    console.log('Current path:', location.pathname);

    // Lấy user info từ localStorage (compatible với cả old và new system)
    const oldUserInfo = AuthService.getUser();
    const newUserInfo = JSON.parse(localStorage.getItem('quanly_truonghoc_user') || '{}');
    const userInfo = newUserInfo?.UserId ? newUserInfo : oldUserInfo;

    // Lấy permissions từ localStorage (bao gồm cả role permissions và user permissions)
    const oldPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const newPermissions = JSON.parse(localStorage.getItem('quanly_truonghoc_permissions') || '[]');
    const rolePermissions = newPermissions.length > 0 ? newPermissions : oldPermissions;

    // Lấy user permissions riêng (quyền cá nhân được phân riêng cho user)
    const oldUserPermissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
    const newUserPermissions = JSON.parse(localStorage.getItem('quanly_truonghoc_user_permissions') || '[]');
    const userPermissions = newUserPermissions.length > 0 ? newUserPermissions : oldUserPermissions;

    // Kết hợp cả role permissions và user permissions
    const allPermissions = [...rolePermissions, ...userPermissions];

    console.log('User info:', userInfo);
    console.log('Role permissions:', rolePermissions);
    console.log('User permissions:', userPermissions);
    console.log('All permissions combined:', allPermissions);

    // Kiểm tra admin role
    const roleKey = (userInfo?.RoleKey || userInfo?.roleKey || '').toLowerCase();
    const roleName = (userInfo?.RoleName || userInfo?.roleName || '').toLowerCase(); 
    const roleId = userInfo?.RoleId || userInfo?.roleId;
    
    const isAdmin = roleKey === 'admin' || 
                    roleName === 'admin' || 
                    roleId === 1;

    console.log('Admin check:', { roleKey, roleName, roleId, isAdmin });

    if (isAdmin) {
      console.log('✅ User is admin - granting access');
      console.log('🔍 === PERMISSION ROUTE CHECK END - ADMIN ACCESS ===');
      return { hasPermission: true, reason: 'Admin user' };
    }

    // Kiểm tra quyền module cụ thể (từ cả role permissions và user permissions)
    const normalizedRequiredModule = requiredModule.toLowerCase();
    
    const hasModulePermission = allPermissions.some((p: any) => {
      const moduleKey = (p.ModuleKey || p.modulekey || p.Module || p.module || '').toLowerCase();
      
      if (moduleKey !== normalizedRequiredModule) {
        return false;
      }

      // Kiểm tra loại quyền theo yêu cầu
      switch (requiredPermission) {
        case 'view':
          return p.CanView || p.canView || p.View || p.view || 
                 p.CanAll || p.canAll || p.All || p.all;
        case 'edit':
          return p.CanEdit || p.canEdit || p.Edit || p.edit || 
                 p.CanAll || p.canAll || p.All || p.all;
        case 'delete':
          return p.CanDelete || p.canDelete || p.Delete || p.delete || 
                 p.CanAll || p.canAll || p.All || p.all;
        case 'all':
          return p.CanAll || p.canAll || p.All || p.all;
        default:
          return false;
      }
    });

    console.log('Module permission check:', { 
      normalizedRequiredModule, 
      hasModulePermission, 
      requiredPermission 
    });

    // Luôn cho phép profile và dashboard
    if (normalizedRequiredModule === 'profile_module' || normalizedRequiredModule === 'dashboard_module') {
      console.log('✅ Always allow profile/dashboard access');
      console.log('🔍 === PERMISSION ROUTE CHECK END - BASIC ACCESS ===');
      return { hasPermission: true, reason: 'Basic access module' };
    }

    if (hasModulePermission) {
      console.log('✅ User has required module permission');
      console.log('🔍 === PERMISSION ROUTE CHECK END - MODULE ACCESS ===');
      return { hasPermission: true, reason: 'Module permission granted' };
    }

    console.log('❌ Access denied - insufficient permissions');
    console.log('🔍 === PERMISSION ROUTE CHECK END - ACCESS DENIED ===');
    return { 
      hasPermission: false, 
      reason: `Không có quyền ${requiredPermission} cho module ${requiredModule}` 
    };

  }, [requiredModule, requiredPermission, location.pathname]);

  if (!permissionCheck.hasPermission) {
    return (
      <div style={{ 
        padding: '50px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <Result
          status="403"
          title="403 - Không có quyền truy cập"
          subTitle={permissionCheck.reason}
          extra={
            <Button type="primary" onClick={() => window.location.href = fallbackPath}>
              Quay về trang chủ
            </Button>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default PermissionRoute;
